import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import MuiBox from '@material-ui/core/Box';
import MuiChip from '@material-ui/core/Chip';
import MuiButton from '@material-ui/core/Button';
import MuiTypography from '@material-ui/core/Typography';
import FilterListIcon from '@material-ui/icons/FilterList';
import { DateTime } from 'luxon';

const styles = theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    marginBottom: theme.spacing(1),
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    '& svg': {
      marginRight: theme.spacing(0.5),
    },
  },
  filtersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  clearButton: {
    marginLeft: 'auto',
  },
});

/**
 * Component to display active filters for Report Currencies table
 */
const ReportCurrencyActiveFilters = props => {
  const { classes, filters, columns, onClearFilter, onClearAllFilters } = props;

  // Function to format filter value for display
  const formatFilterValue = (columnId, filterValues) => {
    const column = columns.find(col => col.id === columnId);
    if (!column) return '';

    if (filterValues.value !== undefined) {
      switch (column.filterType) {
        case 'text':
          return filterValues.value;

        case 'month':
          // Format month display as "Jan 2024"
          try {
            const date = DateTime.fromISO(`${filterValues.value}-01`);
            return date.toFormat('MMM yyyy');
          } catch (e) {
            return filterValues.value;
          }

        default:
          return filterValues.value;
      }
    }

    return JSON.stringify(filterValues);
  };

  // If there are no active filters, don't render anything
  if (!filters || Object.keys(filters).length === 0) {
    return null;
  }

  return (
    <MuiBox className={classes.root}>
      <MuiTypography variant="body2" className={classes.label}>
        <FilterListIcon fontSize="small" />
        Active filters:
      </MuiTypography>

      <MuiBox className={classes.filtersContainer}>
        {Object.entries(filters).map(([columnId, filterValues]) => {
          const column = columns.find(col => col.id === columnId);
          if (!column) return null;

          return (
            <MuiChip
              key={columnId}
              label={`${column.label}: ${formatFilterValue(
                columnId,
                filterValues
              )}`}
              onDelete={() => onClearFilter(columnId)}
              className={classes.chip}
              size="small"
              color="primary"
              variant="outlined"
            />
          );
        })}
      </MuiBox>

      <MuiButton
        size="small"
        onClick={onClearAllFilters}
        className={classes.clearButton}>
        Clear all
      </MuiButton>
    </MuiBox>
  );
};

ReportCurrencyActiveFilters.propTypes = {
  classes: PropTypes.object.isRequired,
  filters: PropTypes.object,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      filterType: PropTypes.string.isRequired,
    })
  ).isRequired,
  onClearFilter: PropTypes.func.isRequired,
  onClearAllFilters: PropTypes.func.isRequired,
};

ReportCurrencyActiveFilters.defaultProps = {
  filters: {},
};

export default withStyles(styles)(ReportCurrencyActiveFilters);
